<template>
  <ValidationObserver v-if="!loading" ref="form" tag="div" class="loan-container">
    <form @submit.prevent="onSubmit" class="form">
      <v-slide-y-transition group tag="div">
        <section key="title" v-if="ready" class="content line-height--14">
          <h1 class="font-size--24 font-weight-black color--nice-black">
            신청 가능한 신용대출을 찾기 위해
            <br v-if="$vuetify.breakpoint.width > 320" />몇가지 정보가 필요합니다.
          </h1>
          <p class="font-size--16 font-weight-bold color--nice-grey">
            아래 사업 현황을 최근 3개월 평균 기준으로 작성해주세요. 작성하신 내용을 기준으로 신청
            가능하신 금융 기관(펀다에이아이 포함)과 대출 한도, 이율의 예상치를 알려드립니다.
          </p>
        </section>

        <section key="form" v-if="ready" class="px-3">
          <ValidationProvider rules="required" v-slot="{ errors }" name="월매출 규모">
            <v-text-field
              v-model="form.monthlySales"
              type="text"
              inputmode="decimal"
              color="primary"
              label="월매출 규모"
              placeholder="월매출 규모를 입력해주세요"
              suffix="만원"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :error-messages="errors"
              :error="errors.length > 0"
            />
          </ValidationProvider>

          <ValidationProvider rules="required" v-slot="{ errors }" name="월 인건비">
            <v-text-field
              v-model="form.monthlyLaborCost"
              type="text"
              inputmode="decimal"
              color="primary"
              label="월 인건비"
              placeholder="월 인건비를 입력해주세요"
              suffix="만원"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :error-messages="errors"
              :error="errors.length > 0"
            />
          </ValidationProvider>

          <ValidationProvider rules="required" v-slot="{ errors }" name="월 임대료 및 관리비">
            <v-text-field
              v-model="form.monthlyRentAndManagementExpenses"
              type="text"
              inputmode="decimal"
              color="primary"
              label="월 임대료 및 관리비"
              placeholder="월 임대료 및 관리비를 입력해주세요"
              suffix="만원"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :error-messages="errors"
              :error="errors.length > 0"
            />
          </ValidationProvider>

          <ValidationProvider rules="required" v-slot="{ errors }" name="기타 매월 필요 경비">
            <v-text-field
              v-model="form.otherMonthlyExpenses"
              type="text"
              inputmode="decimal"
              color="primary"
              label="기타 매월 필요 경비"
              placeholder="기타 매월 필요 경비를 입력해주세요"
              suffix="만원"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :error-messages="errors"
              :error="errors.length > 0"
            />
          </ValidationProvider>

          <ValidationProvider rules="required" v-slot="{ errors }" name="현재 보유 대출 금액">
            <v-text-field
              v-model="form.existingLoanAmount"
              type="text"
              inputmode="decimal"
              color="primary"
              label="현재 보유 대출 금액"
              placeholder="현재 보유 대출 금액을 입력해주세요"
              suffix="만원"
              oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
              :error-messages="errors"
              :error="errors.length > 0"
            />
          </ValidationProvider>
        </section>
      </v-slide-y-transition>

      <v-footer fixed class="mb-4 px-0">
        <v-layout class="pt-2 pb-7 px-3">
          <v-btn
            color="primary"
            class="font--m-3 rounded-4"
            height="52"
            type="submit"
            block
            depressed
          >
            {{ loading ? '조회중 입니다.' : '사업자 신용대출 찾아보기' }}
          </v-btn>
        </v-layout>
      </v-footer>
    </form>
  </ValidationObserver>
  <loading v-else />
</template>

<script>
import { mapActions } from 'vuex'
import { getFirstErrorMessage } from '@/plugins/veevalidate'
import { sleep } from '@/util'

export default {
  components: {
    Loading: () => import('@/pages/compare-loans/Loading.vue'),
  },

  data() {
    return {
      ready: false,
      loading: false,
      form: {
        // 월 매출 규모
        monthlySales: '',
        // 월 인건비
        monthlyLaborCost: '',
        // 월 임대료 및 관리비
        monthlyRentAndManagementExpenses: '',
        // 기타 매월 필요 경비
        otherMonthlyExpenses: '',
        // 현재 보유 대출 금액
        existingLoanAmount: '',
      },
    }
  },
  computed: {
    saveForm() {
      return {
        monthlySales: this.toAmount(this.form.monthlySales),
        monthlyLaborCost: this.toAmount(this.form.monthlyLaborCost),
        monthlyRentAndManagementExpenses: this.toAmount(this.form.monthlyRentAndManagementExpenses),
        otherMonthlyExpenses: this.toAmount(this.form.otherMonthlyExpenses),
        existingLoanAmount: this.toAmount(this.form.existingLoanAmount),
      }
    },
  },
  mounted() {
    this.ready = true
  },
  methods: {
    ...mapActions('loanMarketplace', ['createLoanOffers']),

    async onSubmit() {
      if (this.loading) {
        return
      }

      try {
        this.loading = true
        // 폼 유효성 검사
        this.$nextTick(async () => {
          const isValid = await this.$refs.form.validate()
          if (!isValid) {
            await this.$swal.basic.alert({
              html: `${getFirstErrorMessage(this.$refs.form.errors)}`,
            })
            return
          }

          await sleep(500)

          await this.createLoanOffers(this.saveForm)
          await this.$router.push({ name: 'compare-loans' })
        })
      } finally {
        this.loading = false
      }
    },
    getFirstErrorMessage,
    toAmount(value) {
      return Number(value.replace(/[^\d]/g, '')) * 10000
    },
  },
}
</script>

<style lang="scss" scoped></style>
